import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useScrollHandlerGroup from '@hooks/useScrollHandlerGroup';
import Banner from '@cont/TalkToUnacademy/Banner';
import { useIsMobile } from '@stores/AppCommonData';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsUserSubscribedToStoreBundle } from '@api/selectors/subscription';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import segmentStorage from '@events/segmentStorage';
import { localStorageAvailable } from '@utils/localStorage';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import CbtVenueSelectionScreen from '@cont/SeeAll/TestSeriesListing/TestSeriesRevamp/CbtVenueSelectionScreen';
import { useStoreCbtCheckout } from '@cont/SeeAll/TestSeriesListing/TestSeriesRevamp/useCbtVenueCheckout';
import useCbtVenueScreen from '@cont/SeeAll/useCbtVenueScreen';
import { requiresCentreSelection } from '@cont/SeeAll/utils';
import { CBT_RESERVE_FLOW } from '@cont/SeeAll/constants';
import Header from './Header';
import {
  BLOCK_TYPE,
  BUNDLE_BUNDLE_VIEWED,
  contentType,
  SCROLL_OFFSET_TOP
} from './constants';
import Tabs from './NotesTabs';
import About from './About';
import NotesDetails from './NotesDetails';
import NotesInfo from './NotesInfo';
import FAQSection from './FAQSection';

const Wrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const InnerWrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: grid;
  grid-template-columns: 1fr 336px;
  grid-column-gap: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    display: block;
    width: 100%;
    padding: ${Spacings.SPACING_0B};
  }
`;

const TTUBanner = styled(Banner)`
  margin: 0px;
  margin-top: ${Spacings.SPACING_13B};
  padding: 0px;
  padding-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_3B} !important;
    margin-bottom: ${Spacings.SPACING_3B} !important;
    padding: ${Spacings.SPACING_0B} !important;
  }
`;

const Sections = styled.div``;

const NotesListing = ({ data }) => {
  const [isStickyHeaderShown, setIsStickyHeaderShown] = useState(false);
  const {
    showCbtVenueSelection,
    openCbtVenueSelection,
    closeCbtVenueSelection
  } = useCbtVenueScreen();
  const { query } = useRouter();
  const comboDetails = data.find(
    (value) => value.blockType === 'combo_details'
  );
  const {
    blockData: { goal, skuDetails, isSellable, uid, items, title } = {}
  } = comboDetails ?? {};
  const testSeriesBlock = data.find(
    (block) => block.blockType === BLOCK_TYPE.TEST_SERIES
  )?.blockData;
  const cbtInfo = testSeriesBlock
    ? {
        programmeType: testSeriesBlock.programmeType,
        programmeComposition: testSeriesBlock.programmeComposition,
        seatAvailable: testSeriesBlock.seatAvailable,
        programmeUid: testSeriesBlock.uid
      }
    : null;
  const isCbtCombo = requiresCentreSelection(
    cbtInfo?.programmeType,
    cbtInfo?.programmeComposition
  );
  const goalUID = goal?.uid;
  const goalSegmentData = useGoalEventProps({ goalUID });
  const { proceedToCheckout, getUserDetailsSubmittedProps } =
    useStoreCbtCheckout({
      programmeUid: isCbtCombo ? cbtInfo?.programmeUid : null,
      goalUid: goal?.uid,
      skuUid: skuDetails?.uid,
      reserveFlow: CBT_RESERVE_FLOW.COMBO,
      goalSegmentData,
      programmeComposition: cbtInfo?.programmeComposition,
      entityProps: {
        entity_content_type: 'Notes Combo',
        entity_uid: uid,
        entity_title: title,
        subscription_type: 'Store Subscription',
        offer_price: skuDetails?.price,
        offer_end_date: skuDetails?.priceEffectiveTill ?? 'NA',
        subscription_price: skuDetails?.maxRetailPrice || skuDetails?.price
      },
      getSegmentProps: (venueSelection) => {
        let notesSegmentData = {};
        if (localStorageAvailable()) {
          notesSegmentData = JSON.parse(
            localStorage.getItem('notesSegmentData') || '{}'
          );
        }
        return {
          ...goalSegmentData,
          ...notesSegmentData,
          entity_content_type: 'Notes Combo',
          ...(venueSelection && {
            selected_state_name: venueSelection.state,
            selected_city_name: venueSelection.city,
            selected_test_centre_name: venueSelection.centreName,
            learner_legal_name: venueSelection.fullName.trim()
          })
        };
      }
    });

  const hasActiveNotesSubscription = useMyInfo(
    getIsUserSubscribedToStoreBundle(query.noteUID, '600')
  );

  const isMobile = useIsMobile();
  const appDownloadLink = useOneLinkSmartScriptV2();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const notesEntitySubType = items
    ?.map((value) => contentType[value.contentTypeId])
    ?.join(',');

  useEffect(() => {
    const notesSegmentData = {
      is_sellable: isSellable,
      is_purchased: hasActiveNotesSubscription,
      entity_content_subtype: notesEntitySubType,
      entity_uid: uid,
      entity_title: title
    };

    if (localStorageAvailable()) {
      localStorage.setItem(
        'notesSegmentData',
        JSON.stringify(notesSegmentData)
      );
    }
    const lastPrimarySource = segmentStorage.getItem('lps');
    segment.track(BUNDLE_BUNDLE_VIEWED, {
      ...goalSegmentData,
      ...notesSegmentData,
      last_primary_source: lastPrimarySource
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalSegmentData]);

  useEffect(() => {
    const { redirect } = query;
    if (isMobile && redirect && appDownloadLink) {
      window.open(appDownloadLink, '_blank');
    }
  }, [query, appDownloadLink, isMobile]);

  const [accessDuration, setAccessDuration] = useState('');

  const aboutRef = useRef();
  const physicalNotesRef = useRef();
  const digitalPDFNotesRef = useRef();
  const testsRef = useRef();
  const FAQsRef = useRef();

  const getRefs = (blockType) => {
    switch (blockType) {
      case BLOCK_TYPE.ABOUT:
        return aboutRef;
      case BLOCK_TYPE.PHYSICAL:
        return physicalNotesRef;
      case BLOCK_TYPE.DIGITAL:
        return digitalPDFNotesRef;
      case BLOCK_TYPE.TEST_SERIES:
        return testsRef;
      case BLOCK_TYPE.FAQS:
        return FAQsRef;
      default:
        return null;
    }
  };

  const getIDs = (blockType) => {
    switch (blockType) {
      case BLOCK_TYPE.ABOUT:
        return 'ABOUT';
      case BLOCK_TYPE.PHYSICAL:
        return 'PHYSICAL_NOTES';
      case BLOCK_TYPE.DIGITAL:
        return 'DIGITAL_NOTES';
      case BLOCK_TYPE.TEST_SERIES:
        return 'TESTS';
      case BLOCK_TYPE.FAQS:
        return 'FAQS';
      default:
        return null;
    }
  };

  const refs = [
    {
      ref: aboutRef,
      id: 'ABOUT'
    },
    {
      ref: physicalNotesRef,
      id: 'PHYSICAL_NOTES'
    },
    {
      ref: digitalPDFNotesRef,
      id: 'DIGITAL_NOTES'
    },
    {
      ref: testsRef,
      id: 'TESTS'
    },
    {
      ref: FAQsRef,
      id: 'FAQS'
    }
  ];

  const { scrollToElement } = useScrollHandlerGroup(refs, {
    offsetTop: SCROLL_OFFSET_TOP
  });

  const scrollToRef = (ref, id) => {
    scrollToElement(ref.current, id);
  };

  const handleChange = (tabIndex) => {
    scrollToRef(
      getRefs(data[tabIndex].blockType),
      getIDs(data[tabIndex].blockType)
    );
    setSelectedTabIndex(tabIndex);
  };

  if (showCbtVenueSelection && isCbtCombo) {
    return (
      <CbtVenueSelectionScreen
        programmeUid={cbtInfo.programmeUid}
        onBack={closeCbtVenueSelection}
        onProceed={proceedToCheckout}
        getSegmentProps={getUserDetailsSubmittedProps}
      />
    );
  }

  return (
    <Wrapper>
      <OneLinkSmartScript />
      <Header
        data={
          data?.find((block) => {
            return block.blockType === BLOCK_TYPE.ABOUT;
          })?.blockData
        }
        setIsStickyHeaderShown={setIsStickyHeaderShown}
        setAccessDuration={setAccessDuration}
        cbtInfo={isCbtCombo ? cbtInfo : null}
        onOpenCbtVenueSelection={isCbtCombo ? openCbtVenueSelection : undefined}
      />
      <Sections>
        <Tabs
          tabsData={data}
          onChange={handleChange}
          selectedTabIndex={selectedTabIndex}
        />

        <InnerWrapper>
          <div>
            {data.map((block) => {
              const { blockType, blockData } = block;
              if (blockType === 'combo_details') {
                return (
                  <About
                    key={blockType}
                    ref={aboutRef}
                    data={blockData}
                    goalSegmentData={goalSegmentData}
                  />
                );
              }

              if (blockType === 'faqs') {
                return (
                  <FAQSection
                    key={blockType}
                    ref={getRefs(blockType)}
                    data={blockData}
                    type={blockType}
                  />
                );
              }

              return (
                <NotesInfo
                  key={blockType}
                  type={blockType}
                  ref={getRefs(blockType)}
                  data={blockData}
                  accessDuration={accessDuration}
                  appDownloadLink={appDownloadLink}
                  goalSegmentData={goalSegmentData}
                />
              );
            })}

            {!hasActiveNotesSubscription && (
              <TTUBanner
                ttuConfig={{
                  title: 'Have any questions?',
                  subTitle:
                    'Our academic experts will answer all your questions over a quick phone call.'
                }}
              />
            )}
          </div>

          {!isMobile && (
            <NotesDetails
              data={
                data?.find((block) => {
                  return block.blockType === BLOCK_TYPE.ABOUT;
                })?.blockData
              }
              isSticky
              fromStore
              isHeaderShown={isStickyHeaderShown}
              cbtInfo={isCbtCombo ? cbtInfo : null}
              onOpenCbtVenueSelection={
                isCbtCombo ? openCbtVenueSelection : undefined
              }
            />
          )}
        </InnerWrapper>
      </Sections>
    </Wrapper>
  );
};

export default NotesListing;
